import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaTrash, FaEnvelopeOpenText, FaEnvelope, FaPhone } from 'react-icons/fa';
import axiosInstance from '../../../shared/utils.js/axiosConfig';
import { BASE_URL } from '../../../shared/utils.js/utils';
import { useTitle } from '../../../shared/hooks/TitleContext';

const COLORS = {
  accent: '#AC2000',
  neutral: '#B3B3B3',
};

const WebsiteMessages = () => {
  const { setTitle } = useTitle();
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [messageToDelete, setMessageToDelete] = useState(null);

  const fetchMessages = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/website-contact-messages`, {
        params: { page, per_page: 10 },
      });
      const payload = res.data?.data || {};
      setMessages(Array.isArray(payload.data) ? payload.data : []);
      setPagination({
        current_page: payload.current_page || 1,
        last_page: payload.last_page || 1,
      });
    } catch (error) {
      toast.error('تعذر تحميل رسائل الموقع');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setTitle('رسائل الموقع الإلكتروني');
  }, [setTitle]);

  useEffect(() => {
    fetchMessages(currentPage);
  }, [currentPage, fetchMessages]);

  const handleMarkRead = async (message) => {
    if (message.is_read) return;
    try {
      await axiosInstance.patch(`${BASE_URL}/website-contact-messages/${message.id}/read`);
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, is_read: true } : m)));
    } catch (error) {
      toast.error('تعذر تحديث حالة الرسالة');
    }
  };

  const handleDelete = async () => {
    if (!messageToDelete) return;
    try {
      await axiosInstance.delete(`${BASE_URL}/website-contact-messages/${messageToDelete.id}`);
      toast.success('تم حذف الرسالة بنجاح');
      setMessageToDelete(null);
      fetchMessages(currentPage);
    } catch (error) {
      toast.error('تعذر حذف الرسالة');
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return d.toLocaleString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const cardStyle = {
    background: '#1b3350',
    borderRadius: 12,
    padding: 20,
    marginBottom: 14,
    border: '1px solid #2c4a70',
    position: 'relative',
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <h1 style={{ margin: '0 0 20px', fontSize: 20 }}>رسائل الموقع الإلكتروني</h1>

      {isLoading ? (
        <div style={{ textAlign: 'center', color: COLORS.neutral }}>جاري التحميل...</div>
      ) : messages.length === 0 ? (
        <div style={{ textAlign: 'center', color: COLORS.neutral, padding: 40 }}>لا توجد رسائل حتى الآن</div>
      ) : (
        messages.map((message) => (
          <div
            key={message.id}
            style={{
              ...cardStyle,
              borderRight: message.is_read ? '4px solid #333' : `4px solid ${COLORS.accent}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                  {message.name}
                  {!message.is_read && (
                    <span style={{ background: COLORS.accent, color: 'white', fontSize: 11, borderRadius: 10, padding: '2px 8px', marginRight: 8 }}>
                      جديدة
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: COLORS.neutral, fontSize: 13, marginBottom: 10 }}>
                  <a href={`tel:${message.phone}`} style={{ color: COLORS.neutral, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FaPhone /> {message.phone}
                  </a>
                  {message.email && (
                    <a href={`mailto:${message.email}`} style={{ color: COLORS.neutral, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FaEnvelope /> {message.email}
                    </a>
                  )}
                  <span>{formatDate(message.created_at)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!message.is_read && (
                  <button
                    onClick={() => handleMarkRead(message)}
                    title="تحديد كمقروءة"
                    style={{ background: '#2f3644', color: 'white', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer' }}
                  >
                    <FaEnvelopeOpenText />
                  </button>
                )}
                <button
                  onClick={() => setMessageToDelete(message)}
                  title="حذف"
                  style={{ background: '#c0392b', color: 'white', border: 'none', borderRadius: 6, padding: '8px 10px', cursor: 'pointer' }}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
            <p style={{ margin: 0, color: '#e0e0e0', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{message.message}</p>
          </div>
        ))
      )}

      {pagination && pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={pagination.current_page === 1}
            style={{ padding: '8px 12px', backgroundColor: pagination.current_page === 1 ? '#333' : COLORS.accent, color: 'white', border: 'none', borderRadius: 4, cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer' }}
          >
            السابق
          </button>
          <span style={{ color: COLORS.neutral, alignSelf: 'center' }}>
            صفحة {pagination.current_page} من {pagination.last_page}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(pagination.last_page, p + 1))}
            disabled={pagination.current_page === pagination.last_page}
            style={{ padding: '8px 12px', backgroundColor: pagination.current_page === pagination.last_page ? '#333' : COLORS.accent, color: 'white', border: 'none', borderRadius: 4, cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer' }}
          >
            التالي
          </button>
        </div>
      )}

      {messageToDelete && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setMessageToDelete(null)}
        >
          <div style={{ background: '#1b3350', borderRadius: 12, padding: 24, minWidth: 360, border: '1px solid #333' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>حذف الرسالة</h3>
            <p style={{ color: COLORS.neutral, marginBottom: 20 }}>
              هل أنت متأكد من حذف رسالة {messageToDelete.name}؟
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setMessageToDelete(null)} style={{ background: 'transparent', color: 'white', border: '1px solid #444', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' }}>
                إلغاء
              </button>
              <button onClick={handleDelete} style={{ background: '#c0392b', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer' }}>
                تأكيد الحذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteMessages;
